import { genericApplicationMessage } from '@cdf/api-client-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Duck-type API errors so bundled class identity does not break instanceof. */
function getHttpStatus(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null || !('status' in err)) return undefined;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Extract a human-readable message from an unknown error value.
 * Handles native Errors, the ApiError thrown by the Orval mutator, and the
 * OpenAPI `Error` body shape ({ error, code? }).
 */
function isAuthFailureMessage(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('token validation failed') ||
    m.includes('token expired') ||
    m.includes('token is not active') ||
    m.includes('missing bearer token') ||
    m.includes('session expired') ||
    m.includes('jwt expired') ||
    m.includes('invalid token')
  );
}

export function getErrorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'code' in err && err.code === 'FEATURE_DISABLED') {
    return 'This feature is off. You can still review and finish existing authorized work.';
  }
  if (getHttpStatus(err) === 401) {
    return 'Your session expired. Please sign in again.';
  }
  if (err instanceof Error) {
    if (isAuthFailureMessage(err.message)) {
      return 'Your session expired. Please sign in again.';
    }
    return genericApplicationMessage(err.message);
  }
  if (typeof err === 'object' && err !== null && 'error' in err) {
    const message = (err as { error: unknown }).error;
    if (typeof message === 'string') {
      if (isAuthFailureMessage(message)) {
        return 'Your session expired. Please sign in again.';
      }
      return genericApplicationMessage(message);
    }
  }
  return 'Something went wrong';
}
